import type { MrpPurchaseOrderLine } from "@/lib/mrp";
import type { MvPoLineForm } from "@/lib/purchaseOrderDraftForm";
import { trimText } from "@/lib/utils";

export function normalizePurchaseOrderSku(value: string | null | undefined): string {
  return trimText(value).toLowerCase();
}

export function normalizeCommercialUnitPrice(unitPrice: number | null | undefined): string {
  if (unitPrice == null || !Number.isFinite(unitPrice)) {
    return "";
  }
  return unitPrice.toFixed(4);
}

export function normalizeCommercialDescription(description: string | null | undefined): string {
  return (description ?? "").trim().toLowerCase();
}

export function purchaseOrderCommercialLineKey(input: {
  sku: string;
  unit_price: number | null;
  description: string | null;
}): string {
  const sku = normalizePurchaseOrderSku(input.sku);
  const price = normalizeCommercialUnitPrice(input.unit_price);
  const description = normalizeCommercialDescription(input.description);
  return `${sku}\0${price}\0${description}`;
}

export function commercialLineKeyFromSavedLine(line: MrpPurchaseOrderLine): string {
  return purchaseOrderCommercialLineKey({
    sku: line.cpn,
    unit_price: line.unit_price,
    description: line.description,
  });
}

export function commercialLineKeyFromDraftLine(line: MvPoLineForm): string {
  const unitPriceRaw = trimText(line.unit_price);
  const unitPrice =
    unitPriceRaw === "" ? null : Number.parseFloat(unitPriceRaw);
  return purchaseOrderCommercialLineKey({
    sku: trimText(line.sku),
    unit_price: Number.isFinite(unitPrice) ? unitPrice : null,
    description: trimText(line.line_description) === "" ? null : trimText(line.line_description),
  });
}

export interface PurchaseOrderSkuLineGroup {
  sku: string;
  normalizedSku: string;
  lines: MrpPurchaseOrderLine[];
}

export function groupPurchaseOrderLinesBySku(lines: MrpPurchaseOrderLine[]): PurchaseOrderSkuLineGroup[] {
  const bySku = new Map<string, MrpPurchaseOrderLine[]>();
  const order: string[] = [];
  for (const line of lines) {
    const normalizedSku = normalizePurchaseOrderSku(line.cpn);
    if (normalizedSku === "") continue;
    if (!bySku.has(normalizedSku)) {
      bySku.set(normalizedSku, []);
      order.push(normalizedSku);
    }
    bySku.get(normalizedSku)!.push(line);
  }
  return order.map((normalizedSku) => {
    const groupLines = bySku.get(normalizedSku)!;
    return {
      sku: groupLines[0]!.cpn,
      normalizedSku,
      lines: groupLines,
    };
  });
}

export interface DraftLineIndexGroup {
  sku: string;
  normalizedSku: string;
  lineIndices: number[];
}

/** Stable React key for a draft SKU group — survives partial SKU edits while typing. */
export function draftSkuGroupReactKey(group: DraftLineIndexGroup, lines: MvPoLineForm[]): string {
  return group.lineIndices
    .map((index) => lines[index]?.client_id ?? `idx-${String(index)}`)
    .join("|");
}

export function groupDraftLineIndicesBySku(lines: MvPoLineForm[]): DraftLineIndexGroup[] {
  const groups: DraftLineIndexGroup[] = [];
  const skuToGroupIndex = new Map<string, number>();

  lines.forEach((line, index) => {
    const normalizedSku = normalizePurchaseOrderSku(line.sku);
    if (normalizedSku === "") {
      groups.push({
        sku: "",
        normalizedSku: `__draft-empty-${index}`,
        lineIndices: [index],
      });
      return;
    }
    const existingGroupIndex = skuToGroupIndex.get(normalizedSku);
    if (existingGroupIndex != null) {
      groups[existingGroupIndex]!.lineIndices.push(index);
      return;
    }
    skuToGroupIndex.set(normalizedSku, groups.length);
    groups.push({
      sku: trimText(line.sku),
      normalizedSku,
      lineIndices: [index],
    });
  });

  return groups;
}

function draftLinesShareIntentionalSkuSplit(a: MvPoLineForm, b: MvPoLineForm): boolean {
  const groupA = a.sku_split_group_id?.trim();
  const groupB = b.sku_split_group_id?.trim();
  return groupA != null && groupA !== "" && groupA === groupB;
}

/**
 * Saved Megaventory POs persist duplicate SKUs as separate line items but not the hub-only
 * `sku_split_group_id`. Re-apply split groups so reload/autosave does not flag intentional splits.
 */
export function assignSkuSplitGroupIdsForDuplicateSkus(lines: MvPoLineForm[]): MvPoLineForm[] {
  const skuCounts = new Map<string, number>();
  for (const line of lines) {
    if (line.is_delivery_segment) continue;
    const normalizedSku = normalizePurchaseOrderSku(line.sku);
    if (normalizedSku === "") continue;
    skuCounts.set(normalizedSku, (skuCounts.get(normalizedSku) ?? 0) + 1);
  }

  const groupIdBySku = new Map<string, string>();
  return lines.map((line) => {
    if (line.is_delivery_segment) return line;
    const normalizedSku = normalizePurchaseOrderSku(line.sku);
    if (normalizedSku === "" || (skuCounts.get(normalizedSku) ?? 0) < 2) {
      return line;
    }
    let groupId = groupIdBySku.get(normalizedSku);
    if (groupId == null) {
      groupId = crypto.randomUUID();
      groupIdBySku.set(normalizedSku, groupId);
    }
    if (line.sku_split_group_id === groupId) {
      return line;
    }
    return { ...line, sku_split_group_id: groupId };
  });
}

/** Rows that repeat a SKU without using + to split a commercial line. */
export function findDraftAccidentalDuplicateSkuLineIndices(lines: MvPoLineForm[]): Set<number> {
  const offenders = new Set<number>();
  for (let leftIndex = 0; leftIndex < lines.length; leftIndex += 1) {
    if (lines[leftIndex]!.is_delivery_segment) continue;
    const leftSku = normalizePurchaseOrderSku(lines[leftIndex]!.sku);
    if (leftSku === "") continue;
    for (let rightIndex = 0; rightIndex < lines.length; rightIndex += 1) {
      if (leftIndex === rightIndex) continue;
      if (lines[rightIndex]!.is_delivery_segment) continue;
      const rightSku = normalizePurchaseOrderSku(lines[rightIndex]!.sku);
      if (leftSku !== rightSku) continue;
      if (draftLinesShareIntentionalSkuSplit(lines[leftIndex]!, lines[rightIndex]!)) continue;
      const leftSplitGroup = lines[leftIndex]!.sku_split_group_id?.trim();
      const rightSplitGroup = lines[rightIndex]!.sku_split_group_id?.trim();
      if (!leftSplitGroup) offenders.add(leftIndex);
      if (!rightSplitGroup) offenders.add(rightIndex);
      if (
        leftSplitGroup &&
        rightSplitGroup &&
        leftSplitGroup !== rightSplitGroup
      ) {
        offenders.add(leftIndex);
        offenders.add(rightIndex);
      }
    }
  }
  return offenders;
}

export function draftSkuClusterLineIndices(lines: MvPoLineForm[], lineIndex: number): number[] {
  const line = lines[lineIndex];
  if (line == null) return [];
  const splitGroupId = line.sku_split_group_id?.trim();
  if (splitGroupId != null && splitGroupId !== "") {
    return lines
      .map((candidate, index) => (candidate.sku_split_group_id === splitGroupId ? index : -1))
      .filter((index) => index >= 0);
  }
  return [lineIndex];
}

export function isPrimaryDraftSkuClusterLine(lines: MvPoLineForm[], lineIndex: number): boolean {
  const clusterIndices = draftSkuClusterLineIndices(lines, lineIndex);
  return clusterIndices[0] === lineIndex;
}

export function primaryDraftSkuClusterLineIndex(lines: MvPoLineForm[], lineIndex: number): number {
  return draftSkuClusterLineIndices(lines, lineIndex)[0] ?? lineIndex;
}

export function lastDraftSkuClusterLineIndex(lines: MvPoLineForm[], lineIndex: number): number {
  const clusterIndices = draftSkuClusterLineIndices(lines, lineIndex);
  return clusterIndices[clusterIndices.length - 1] ?? lineIndex;
}

export function isLastDraftSkuClusterLine(lines: MvPoLineForm[], lineIndex: number): boolean {
  return lastDraftSkuClusterLineIndex(lines, lineIndex) === lineIndex;
}

/** Where the transfer expanded panel attaches — after delivery segments when present. */
export function isLastTransferPanelAnchorLine(lines: MvPoLineForm[], lineIndex: number): boolean {
  const line = lines[lineIndex];
  if (line == null) return false;
  if (line.is_delivery_segment) {
    const itemId = line.purchase_order_item_id;
    for (let index = lineIndex + 1; index < lines.length; index += 1) {
      const next = lines[index]!;
      if (next.is_delivery_segment && next.purchase_order_item_id === itemId) {
        return false;
      }
      break;
    }
    return true;
  }
  const itemId = line.purchase_order_item_id;
  if (itemId != null) {
    for (let index = lineIndex + 1; index < lines.length; index += 1) {
      const next = lines[index]!;
      if (next.is_delivery_segment && next.purchase_order_item_id === itemId) {
        return false;
      }
      break;
    }
  }
  return isLastDraftSkuClusterLine(lines, lineIndex);
}

export function aggregateOpenQuantityForSavedLines(lines: MrpPurchaseOrderLine[]): number {
  return lines.reduce((sum, line) => sum + Math.max(0, line.quantity_ordered - line.quantity_received), 0);
}

export function earliestExpectedArrivalForSavedLines(lines: MrpPurchaseOrderLine[]): string {
  const dates: string[] = [];
  for (const line of lines) {
    const headerDate = line.expected_arrival?.trim() ?? "";
    if (headerDate !== "") {
      dates.push(headerDate);
      continue;
    }
    for (const segment of line.delivery_segments ?? []) {
      const segmentDate = segment.expected_arrival?.trim() ?? "";
      if (segmentDate !== "") {
        dates.push(segmentDate);
      }
    }
  }
  dates.sort();
  return dates[0] ?? "";
}

export function aggregateDraftQuantityForIndices(lines: MvPoLineForm[], indices: number[]): number {
  return indices.reduce((sum, index) => {
    const parsed = Number.parseFloat(lines[index]?.quantity ?? "");
    return sum + (Number.isFinite(parsed) ? parsed : 0);
  }, 0);
}

/** Order quantity cap for transfer requests on a PO line cluster (includes delivery segment rows). */
export function purchaseOrderClusterLineQuantity(lines: MvPoLineForm[], lineIndex: number): number {
  const primaryIndex = primaryDraftSkuClusterLineIndex(lines, lineIndex);
  const primaryLine = lines[primaryIndex];
  if (primaryLine == null) return 0;
  const purchaseOrderItemId = primaryLine.purchase_order_item_id;
  if (purchaseOrderItemId != null) {
    return lines.reduce((sum, row) => {
      if (row.purchase_order_item_id !== purchaseOrderItemId) return sum;
      const parsed = Number.parseFloat(row.quantity);
      return sum + (Number.isFinite(parsed) ? parsed : 0);
    }, 0);
  }
  return aggregateDraftQuantityForIndices(lines, draftSkuClusterLineIndices(lines, lineIndex));
}

export function sumTransferRequestQuantities(
  transferRequests: MvPoLineForm["transfer_requests"],
): number {
  return transferRequests.reduce((sum, transferRequest) => {
    const parsed = Number.parseInt(transferRequest.quantity, 10);
    return sum + (Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
  }, 0);
}

export function earliestDraftExpectedDelivery(lines: MvPoLineForm[], indices: number[]): string {
  const dates = indices
    .map((index) => trimText(lines[index]?.expected_delivery_date))
    .filter((value) => value !== "")
    .sort();
  return dates[0] ?? "";
}

/** First draft row index for a SKU group — canonical store for SKU-level transfer requests. */
export function primaryDraftLineIndexForSkuGroup(group: DraftLineIndexGroup): number {
  return group.lineIndices[0]!;
}

export function collectDraftTransferRequestsForLineCluster(
  lines: MvPoLineForm[],
  lineIndex: number,
): MvPoLineForm["transfer_requests"] {
  const primaryIndex = primaryDraftSkuClusterLineIndex(lines, lineIndex);
  const primaryRequests = lines[primaryIndex]?.transfer_requests ?? [];
  if (primaryRequests.length > 0) {
    return [...primaryRequests];
  }
  const clusterIndices = draftSkuClusterLineIndices(lines, lineIndex);
  const merged: MvPoLineForm["transfer_requests"] = [];
  const seenClientIds = new Set<string>();
  for (const index of clusterIndices) {
    for (const transferRequest of lines[index]?.transfer_requests ?? []) {
      if (seenClientIds.has(transferRequest.client_id)) continue;
      seenClientIds.add(transferRequest.client_id);
      merged.push(transferRequest);
    }
  }
  return merged;
}

export function collectDraftTransferRequestsForSkuGroup(
  lines: MvPoLineForm[],
  group: DraftLineIndexGroup,
): MvPoLineForm["transfer_requests"] {
  const merged: MvPoLineForm["transfer_requests"] = [];
  const seen = new Set<string>();
  for (const index of group.lineIndices) {
    for (const transferRequest of lines[index]?.transfer_requests ?? []) {
      const signature = [
        transferRequest.quantity,
        transferRequest.destination_location_id,
        transferRequest.target_date,
        transferRequest.reason,
        transferRequest.notes,
      ].join("\0");
      if (seen.has(signature)) continue;
      seen.add(signature);
      merged.push(transferRequest);
    }
  }
  return merged;
}
